import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiFileText, FiFilter, FiChevronUp, FiChevronDown, FiChevronRight,
  FiColumns, FiDownload, FiX, FiInbox, FiSearch, FiMaximize2, FiMinimize2,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { differenceInDays } from 'date-fns';
import { formatDateMedium } from '../../utils/dateFormat';
import { formatMoney, formatNumber } from '../../utils/numberFormat';
import { contractStatusKey } from '../../utils/contractStatus';
import i18n from '../../i18n';
import { getLocalized } from '../../i18n/getLocalized';
import SearchableSelect from '../../components/SearchableSelect';
import useStickyTableHeader from '../../hooks/useStickyTableHeader';
import useFullscreenPanel from '../../hooks/useFullscreenPanel';

// ────────────────────────────────────────────────────────────────────────────
// Column model. `accessor` returns the raw value used for filtering, sorting,
// grouping and export; `render` (optional) draws the cell. Types drive the
// header-filter UI: text → contains, enum → checkbox list of distinct values,
// number → min/max, date → from/to.
// ────────────────────────────────────────────────────────────────────────────

const ownerName = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '');

// Counterparty type rides on the populated counterparty master; its name is a
// multilang map resolved to the active language.
const cpTypeName = (c) => getLocalized(c.counterpartyId?.counterpartyTypeId?.name, i18n.language);

// Same for the contract type name; falls back to the legacy `contractType`
// enum string on contracts created before types became master data.
const typeName = (c) => getLocalized(c.contractTypeId?.name, i18n.language);

// Deadline status derived from the end date: expired / expiring within 30
// days / on track. Contracts without an end date stay blank.
const deadlineKey = (c) => {
  if (!c.endDate) return '';
  const days = differenceInDays(new Date(c.endDate), new Date());
  if (days < 0) return 'expired';
  if (days <= 30) return 'expiring_soon';
  return 'on_track';
};

const COLUMN_DEFS = [
  { key: 'number',       type: 'text',   accessor: (c) => c.contractNumber || '' },
  { key: 'title',        type: 'text',   accessor: (c) => c.contractName || c.counterpartyName || c.contractNumber || '' },
  { key: 'type',         type: 'enum',   groupable: true,  accessor: (c) => typeName(c) || c.contractType || '' },
  // Multi-company: which of the tenant's companies the contract belongs to.
  // Sits ahead of the counterparty columns per the standard column order.
  { key: 'company',      type: 'enum',   groupable: true,  accessor: (c) => c.companyId?.name || '' },
  { key: 'counterpartyType', type: 'enum', groupable: true, accessor: (c) => cpTypeName(c) || '' },
  { key: 'counterparty', type: 'enum',   groupable: true,  accessor: (c) => c.counterpartyName || '' },
  // The name, not the stored value: 'pending_edit' reads as Geri Gönderildi
  // or Uygun Görülmedi depending on how the contract came back, and filtering,
  // grouping and export have to agree with the cell.
  { key: 'status',       type: 'enum',   groupable: true,  accessor: (c) => contractStatusKey(c) },
  { key: 'value',        type: 'number', accessor: (c) => c.contractAmount ?? c.contractValue ?? null },
  { key: 'currency',     type: 'enum',   groupable: true,  accessor: (c) => c.currency || '' },
  { key: 'startDate',    type: 'date',   accessor: (c) => c.startDate || null },
  { key: 'endDate',      type: 'date',   accessor: (c) => c.endDate || null },
  { key: 'deadlineStatus', type: 'enum', groupable: true, accessor: deadlineKey },
  // Days remaining until the end date (fractional, negative once past) —
  // numeric so sorting and min/max filtering work in days.
  { key: 'remaining',    type: 'number', accessor: (c) => (c.endDate ? (new Date(c.endDate).getTime() - Date.now()) / 86400000 : null) },
  // Responsible user when assigned, else the contract's creator.
  { key: 'owner',        type: 'enum',   groupable: true,  accessor: (c) => ownerName(c.responsibleUserId || c.internalOwner || c.createdBy) },
  { key: 'createdBy',    type: 'enum',   groupable: true,  accessor: (c) => ownerName(c.createdBy) },
  { key: 'createdAt',    type: 'date',   accessor: (c) => c.createdAt || null },
  { key: 'updatedAt',    type: 'date',   accessor: (c) => c.updatedAt || null },
  { key: 'priority',     type: 'enum',   groupable: true,  accessor: (c) => c.priority || '' },
  { key: 'confidentialityLevel', type: 'enum', groupable: true, accessor: (c) => c.confidentialityLevel || '' },
  { key: 'tags',         type: 'text',   accessor: (c) => (Array.isArray(c.tags) ? c.tags.join(', ') : '') },
];

const DEFAULT_VISIBLE = [
  'number', 'title', 'type', 'company', 'counterpartyType', 'counterparty', 'status',
  'value', 'endDate', 'deadlineStatus', 'remaining', 'owner',
];
// v2: bumped when the counterpartyType / deadlineStatus / remaining columns
// shipped, so stored preferences from before don't hide them.
// v3: same for the company column.
const VISIBLE_STORAGE_KEY = 'cl:visibleCols.v3';

function loadVisible() {
  try {
    const raw = window.localStorage?.getItem(VISIBLE_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE;
    const parsed = JSON.parse(raw);
    const valid = parsed.filter((k) => COLUMN_DEFS.some((c) => c.key === k));
    return valid.length ? valid : DEFAULT_VISIBLE;
  } catch { return DEFAULT_VISIBLE; }
}

const isFilterActive = (f) => {
  if (f == null) return false;
  if (typeof f === 'string') return f.trim() !== '';
  if (Array.isArray(f)) return f.length > 0;
  return Object.values(f).some((v) => v !== '' && v != null);
};

function ContractsTable({
  items, loading, pagination, pageSize,
  onPageChange, onPageSizeChange,
  openContract, contractPath,
  formatStatus, formatType,
  renderSkeletonRows, renderPagination,
  // The page owns the search term (it drives the query). Full screen hides the
  // page around the table, so the toolbar borrows it there.
  search, onSearchChange,
}) {
  const { t } = useTranslation();

  const [visibleKeys, setVisibleKeys] = useState(loadVisible);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(null);            // { key, dir: 1 | -1 }
  const [groupBy, setGroupBy] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [openMenu, setOpenMenu] = useState(null);    // 'cols' | `filter:${key}` | null
  const [menuPos, setMenuPos] = useState(null);      // fixed-position anchor of the open menu

  // The table grows with its content — vertical navigation is the page's own
  // scroll. Only the horizontal axis scrolls inside the box, and the header
  // stays visible through a pinned copy above it, re-measured whenever the
  // column set or the row count changes the table's shape.
  const stickyHead = useStickyTableHeader([visibleKeys.join('|'), (items || []).length]);

  // Dense working view: the table becomes a fixed panel over the whole
  // viewport and owns its own scrolling. State lives here, so nothing is lost
  // on the way in or out.
  const [fullscreen, setFullscreen] = useFullscreenPanel();

  // Toggle a popover, anchored just below the trigger button. Menus render in
  // a body portal with position:fixed, so neither the scrolling table nor any
  // transformed ancestor (entry animations) can clip or displace them.
  const toggleMenu = (id, e) => {
    if (openMenu === id) { setOpenMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({
      top: Math.min(rect.bottom + 6, window.innerHeight - 320),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 290)),
    });
    setOpenMenu(id);
  };

  useEffect(() => {
    try { window.localStorage?.setItem(VISIBLE_STORAGE_KEY, JSON.stringify(visibleKeys)); } catch { /* noop */ }
  }, [visibleKeys]);

  // Close any open popover on outside click / Escape / scroll (fixed-position
  // menus would drift away from their trigger on scroll). Containment checks
  // use closest() because the menu lives in a body portal, away from the
  // trigger's DOM subtree.
  useEffect(() => {
    if (!openMenu) return;
    const inMenuOrTrigger = (target) =>
      target instanceof Element
      && (target.closest('.cl-th-menu') || target.closest('[data-menu-trigger]'));
    const onDown = (e) => { if (!inMenuOrTrigger(e.target)) setOpenMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    const onScroll = (e) => {
      if (e.target instanceof Element && e.target.closest('.cl-th-menu')) return;
      setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [openMenu]);

  const colLabel = useCallback(
    (key) => t(`contracts.columns.${key}`, { defaultValue: key }),
    [t]
  );

  // "19 hours" under a day, "2 days" from there on; expired contracts say so.
  const remainingLabel = useCallback((contract) => {
    if (!contract.endDate) return '';
    const ms = new Date(contract.endDate).getTime() - Date.now();
    if (ms <= 0) return t('contracts.deadline.expired', { defaultValue: 'Expired' });
    const hours = Math.floor(ms / 3600000);
    if (hours < 24) {
      const n = Math.max(hours, 1);
      return t('contracts.remaining.hours', { n, defaultValue: `${n} hours` });
    }
    const n = Math.round(ms / 86400000);
    return t('contracts.remaining.days', { n, defaultValue: `${n} days` });
  }, [t]);

  // Display formatting per column — used by cells, group headers and export.
  const displayValue = useCallback((col, contract) => {
    const raw = col.accessor(contract);
    switch (col.key) {
      case 'status':   return formatStatus(raw);
      case 'type':     return raw ? (typeName(contract) || formatType(raw) || raw) : '';
      case 'priority': return raw ? t(`contracts.priority.${raw}`, { defaultValue: raw }) : '';
      case 'confidentialityLevel': return raw ? t(`contracts.confidentialityLevel.${raw}`, { defaultValue: raw }) : '';
      case 'deadlineStatus': return raw ? t(`contracts.deadline.${raw}`, { defaultValue: raw }) : '';
      case 'remaining': return remainingLabel(contract);
      default:
        if (col.type === 'date') return raw ? formatDateMedium(raw) : '';
        if (col.type === 'number') return raw != null ? formatNumber(raw, '') : '';
        return raw ?? '';
    }
  }, [formatStatus, formatType, t, remainingLabel]);

  const visibleCols = useMemo(
    () => COLUMN_DEFS.filter((c) => visibleKeys.includes(c.key)),
    [visibleKeys]
  );

  // Distinct raw values per enum column (for the checkbox filter lists).
  const distinctValues = useMemo(() => {
    const map = {};
    for (const col of COLUMN_DEFS) {
      if (col.type !== 'enum') continue;
      const seen = new Set();
      for (const c of items || []) {
        const v = col.accessor(c);
        if (v !== '' && v != null) seen.add(v);
      }
      map[col.key] = [...seen].sort((a, b) => String(a).localeCompare(String(b)));
    }
    return map;
  }, [items]);

  // ── Pipeline: filter → sort → group ──
  const filteredRows = useMemo(() => {
    let rows = items || [];
    for (const [key, f] of Object.entries(filters)) {
      if (!isFilterActive(f)) continue;
      const col = COLUMN_DEFS.find((c) => c.key === key);
      if (!col) continue;
      rows = rows.filter((c) => {
        const raw = col.accessor(c);
        switch (col.type) {
          case 'text':
            return String(raw || '').toLowerCase().includes(f.trim().toLowerCase());
          case 'enum':
            return f.includes(raw);
          case 'number': {
            if (raw == null) return false;
            const n = Number(raw);
            if (f.min !== '' && f.min != null && n < Number(f.min)) return false;
            if (f.max !== '' && f.max != null && n > Number(f.max)) return false;
            return true;
          }
          case 'date': {
            if (!raw) return false;
            const d = new Date(raw).getTime();
            if (f.from && d < new Date(f.from).getTime()) return false;
            if (f.to && d > new Date(f.to).getTime() + 24 * 60 * 60 * 1000 - 1) return false;
            return true;
          }
          default: return true;
        }
      });
    }
    return rows;
  }, [items, filters]);

  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows;
    const col = COLUMN_DEFS.find((c) => c.key === sort.key);
    if (!col) return filteredRows;
    const val = (c) => col.accessor(c);
    return [...filteredRows].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (va == null || va === '') return 1;   // empties last, either direction
      if (vb == null || vb === '') return -1;
      let cmp;
      if (col.type === 'number') cmp = Number(va) - Number(vb);
      else if (col.type === 'date') cmp = new Date(va) - new Date(vb);
      else cmp = String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
      return cmp * sort.dir;
    });
  }, [filteredRows, sort]);

  const groups = useMemo(() => {
    if (!groupBy) return null;
    const col = COLUMN_DEFS.find((c) => c.key === groupBy);
    if (!col) return null;
    const map = new Map();
    for (const c of sortedRows) {
      const label = displayValue(col, c) || t('contracts.table.ungrouped', { defaultValue: '(empty)' });
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(c);
    }
    return [...map.entries()].map(([label, rows]) => ({
      label,
      rows,
      total: rows.reduce((s, c) => s + (Number(c.contractAmount ?? c.contractValue) || 0), 0),
    }));
  }, [groupBy, sortedRows, displayValue, t]);

  const activeFilterCount = Object.values(filters).filter(isFilterActive).length;

  const cycleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 1 };
      if (prev.dir === 1) return { key, dir: -1 };
      return null;
    });
  };

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilter = (key) => setFilters((prev) => {
    const next = { ...prev };
    delete next[key];
    return next;
  });

  const toggleGroup = (label) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  // ── Excel export: exactly what is displayed (filtered + sorted, visible
  //    columns; grouped order when grouping is on) ──
  const exportExcel = () => {
    const cols = visibleCols;
    const header = cols.map((c) => colLabel(c.key));
    const rowToCells = (c) => cols.map((col) => {
      if (col.type === 'number') {
        const raw = col.accessor(c);
        return raw != null ? Number(raw) : '';
      }
      return displayValue(col, c);
    });

    const aoa = [header];
    if (groups) {
      for (const g of groups) {
        aoa.push([`${groupBy ? colLabel(groupBy) : ''}: ${g.label} (${g.rows.length})`]);
        for (const c of g.rows) aoa.push(rowToCells(c));
      }
    } else {
      for (const c of sortedRows) aoa.push(rowToCells(c));
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = cols.map((col) => ({ wch: col.type === 'text' ? 28 : 16 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('contracts.title', { defaultValue: 'Contracts' }).slice(0, 31));
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `contracts_${stamp}.xlsx`);
  };

  // ── Cell renderers (keeps the existing look for the core columns) ──
  const daysUntil = (endDate) => (endDate ? differenceInDays(new Date(endDate), new Date()) : null);

  const renderCell = (col, c) => {
    switch (col.key) {
      case 'number':
        return <td key={col.key} className="cl-row__id"><span>{c.contractNumber}</span></td>;
      case 'title':
        return (
          <td key={col.key} className="cl-row__title-cell">
            <div className="cl-row__title-group">
              <div className="cl-row__file-icon"><FiFileText /></div>
              <div className="cl-row__title-content">
                <Link
                  to={contractPath(c)}
                  className="cl-row__title"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Only the name truncates — the addendum badge sits beside
                      it, or a long title's ellipsis would swallow the badge. */}
                  <span className="cl-row__title-text">
                    {c.contractName || c.counterpartyName || c.contractNumber}
                  </span>
                  {c.versionType === 'addendum' && (
                    <span className="badge badge-addendum">
                      {t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })}
                    </span>
                  )}
                </Link>
                {c.contractName && c.counterpartyName && (
                  <span className="cl-row__subtitle">{c.counterpartyName}</span>
                )}
              </div>
            </div>
          </td>
        );
      case 'type': {
        const label = displayValue(col, c);
        return (
          <td key={col.key}>
            {label ? <span className="cl-type-tag">{label}</span> : <span className="cl-row__muted">—</span>}
          </td>
        );
      }
      case 'status':
        return (
          <td key={col.key}>
            <span className={`badge badge-${c.status}`}>
              {(c.status === 'active' || c.status === 'expiring_soon') && <span className="cl-pulse-dot" />}
              {formatStatus(c)}
            </span>
          </td>
        );
      case 'value': {
        const raw = col.accessor(c);
        return (
          <td key={col.key} className="cl-row__value">
            {raw != null ? (
              <span className="cl-row__amount">{c.currency ? `${c.currency} ` : '$'}{formatMoney(raw)}</span>
            ) : (
              <span className="cl-row__muted">—</span>
            )}
          </td>
        );
      }
      case 'endDate': {
        const days = daysUntil(c.endDate);
        const warn = days !== null && days >= 0 && days <= 30;
        const danger = days !== null && days < 0;
        return (
          <td key={col.key}>
            <span className={`cl-row__text ${warn ? 'cl-row__text--warn' : ''} ${danger ? 'cl-row__text--danger' : ''}`}>
              {formatDateMedium(c.endDate)}
            </span>
          </td>
        );
      }
      case 'deadlineStatus': {
        const key = deadlineKey(c);
        if (!key) return <td key={col.key}><span className="cl-row__muted">—</span></td>;
        const badge = key === 'expired' ? 'badge-expired'
          : key === 'expiring_soon' ? 'badge-expiring_soon'
            : 'badge-active';
        return (
          <td key={col.key}>
            <span className={`badge ${badge}`}>{t(`contracts.deadline.${key}`, { defaultValue: key })}</span>
          </td>
        );
      }
      case 'remaining': {
        const label = displayValue(col, c);
        if (!label) return <td key={col.key}><span className="cl-row__muted">—</span></td>;
        const days = daysUntil(c.endDate);
        const warn = days !== null && days >= 0 && days <= 30;
        const danger = days !== null && days < 0;
        return (
          <td key={col.key}>
            <span className={`cl-row__text ${warn ? 'cl-row__text--warn' : ''} ${danger ? 'cl-row__text--danger' : ''}`}>
              {label}
            </span>
          </td>
        );
      }
      case 'owner': {
        const u = c.responsibleUserId || c.internalOwner || c.createdBy;
        return (
          <td key={col.key}>
            {u ? (
              <div className="cl-row__owner">
                <div className="cl-row__avatar">{u.firstName?.[0]}{u.lastName?.[0]}</div>
                <span>{ownerName(u)}</span>
              </div>
            ) : (
              <span className="cl-row__muted">—</span>
            )}
          </td>
        );
      }
      default: {
        const label = displayValue(col, c);
        return (
          <td key={col.key}>
            {label ? <span className="cl-row__text">{label}</span> : <span className="cl-row__muted">—</span>}
          </td>
        );
      }
    }
  };

  const renderFilterMenu = (col) => {
    const f = filters[col.key];
    return (
      <div
        className="cl-th-menu"
        style={menuPos ? { top: menuPos.top, left: menuPos.left } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {col.type === 'text' && (
          <input
            type="text"
            className="cl-th-menu__input"
            placeholder={t('contracts.table.contains', { defaultValue: 'Contains…' })}
            value={f || ''}
            autoFocus
            onChange={(e) => setFilter(col.key, e.target.value)}
          />
        )}
        {col.type === 'enum' && (
          <div className="cl-th-menu__list">
            {(distinctValues[col.key] || []).length === 0 && (
              <span className="cl-th-menu__empty">{t('contracts.table.noValues', { defaultValue: 'No values' })}</span>
            )}
            {(distinctValues[col.key] || []).map((v) => {
              const selected = Array.isArray(f) && f.includes(v);
              const label = col.key === 'status' ? formatStatus(v)
                : col.key === 'priority' ? t(`contracts.priority.${v}`, { defaultValue: v })
                : col.key === 'confidentialityLevel' ? t(`contracts.confidentialityLevel.${v}`, { defaultValue: v })
                : col.key === 'deadlineStatus' ? t(`contracts.deadline.${v}`, { defaultValue: v })
                : String(v);
              return (
                <label key={v} className="cl-th-menu__option">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {
                      const cur = Array.isArray(f) ? f : [];
                      setFilter(col.key, selected ? cur.filter((x) => x !== v) : [...cur, v]);
                    }}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>
        )}
        {col.type === 'number' && (
          <div className="cl-th-menu__range">
            <input
              type="number"
              className="cl-th-menu__input"
              placeholder={t('contracts.table.min', { defaultValue: 'Min' })}
              value={f?.min ?? ''}
              onChange={(e) => setFilter(col.key, { ...(f || {}), min: e.target.value })}
            />
            <input
              type="number"
              className="cl-th-menu__input"
              placeholder={t('contracts.table.max', { defaultValue: 'Max' })}
              value={f?.max ?? ''}
              onChange={(e) => setFilter(col.key, { ...(f || {}), max: e.target.value })}
            />
          </div>
        )}
        {col.type === 'date' && (
          <div className="cl-th-menu__range">
            <input
              type="date"
              className="cl-th-menu__input"
              value={f?.from || ''}
              onChange={(e) => setFilter(col.key, { ...(f || {}), from: e.target.value })}
            />
            <input
              type="date"
              className="cl-th-menu__input"
              value={f?.to || ''}
              onChange={(e) => setFilter(col.key, { ...(f || {}), to: e.target.value })}
            />
          </div>
        )}
        {isFilterActive(f) && (
          <button type="button" className="cl-th-menu__clear" onClick={() => clearFilter(col.key)}>
            <FiX /> {t('contracts.table.clearFilter', { defaultValue: 'Clear filter' })}
          </button>
        )}
      </div>
    );
  };

  const groupableCols = COLUMN_DEFS.filter((c) => c.groupable);
  // Skeleton rows on every fetch, refetches included — stale rows shown
  // dimmed read as a frozen list while the response is in flight.
  const showSkeleton = loading;
  const colCount = visibleCols.length;

  // One header row, rendered twice: in the table itself, and in the pinned bar
  // above it. `pinned` marks the copy — it must not render the filter popovers
  // a second time (they are portalled and would double up), and it stays out
  // of the tab order because the real header is already in it.
  const renderHeadRow = (pinned) => (
    <tr>
      {visibleCols.map((col) => {
        const sorted = sort?.key === col.key;
        const filtered = isFilterActive(filters[col.key]);
        const thClass = [
          col.key === 'number' ? 'cl-table__th--id' : '',
          col.key === 'title' ? 'cl-table__th--title' : '',
          col.key === 'value' ? 'cl-table__th--right' : '',
          'cl-table__th--interactive',
        ].filter(Boolean).join(' ');
        return (
          <th key={col.key} className={thClass} aria-sort={sorted ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
            <div className="cl-th">
              <button
                type="button"
                className="cl-th__sort"
                tabIndex={pinned ? -1 : undefined}
                onClick={() => cycleSort(col.key)}
              >
                <span>{colLabel(col.key)}</span>
                <span className={`cl-th__sort-icons ${sorted ? 'cl-th__sort-icons--on' : ''}`}>
                  {sorted ? (sort.dir === 1 ? <FiChevronUp /> : <FiChevronDown />) : <FiChevronDown />}
                </span>
              </button>
              <span className="cl-th__filter-anchor">
                <button
                  type="button"
                  data-menu-trigger
                  className={`cl-th__filter ${filtered ? 'cl-th__filter--on' : ''}`}
                  tabIndex={pinned ? -1 : undefined}
                  onClick={(e) => toggleMenu(`filter:${col.key}`, e)}
                  aria-label={t('contracts.table.filterCol', { col: colLabel(col.key), defaultValue: `Filter ${colLabel(col.key)}` })}
                >
                  <FiFilter />
                </button>
                {!pinned && openMenu === `filter:${col.key}`
                  && createPortal(renderFilterMenu(col), document.body)}
              </span>
            </div>
          </th>
        );
      })}
    </tr>
  );

  const table = (
    <div
      className={[
        'cl-table-wrapper',
        fullscreen ? 'cl-table-wrapper--fs' : '',
      ].filter(Boolean).join(' ')}
    >
      {/* Grid toolbar: grouping, column chooser, Excel export */}
      <div className="cl-grid-toolbar">
        <div className="cl-grid-toolbar__left">
          {/* The page's search box is hidden along with the rest of the page in
              full screen, so the toolbar carries it there. Same state — typing
              filters the list exactly as it does on the page. */}
          {fullscreen && onSearchChange && (
            <div className="cl-grid-toolbar__search">
              <FiSearch />
              <input
                type="text"
                value={search || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t('contracts.searchPlaceholder', { defaultValue: 'Search contracts…' })}
              />
              {search && (
                <button type="button" onClick={() => onSearchChange('')} aria-label={t('common.clearFilters')}>
                  <FiX />
                </button>
              )}
            </div>
          )}
          <div className="cl-grid-toolbar__group">
            <SearchableSelect
              value={groupBy}
              onChange={(v) => { setGroupBy(v || ''); setCollapsed(new Set()); }}
              placeholder={t('contracts.table.groupBy', { defaultValue: 'Group by' })}
              options={[
                { value: '', label: t('contracts.table.noGrouping', { defaultValue: 'No grouping' }) },
                ...groupableCols.map((c) => ({
                  value: c.key,
                  label: t('contracts.table.groupByCol', { col: colLabel(c.key), defaultValue: `Group: ${colLabel(c.key)}` }),
                })),
              ]}
            />
          </div>
          {activeFilterCount > 0 && (
            <button type="button" className="cl-filters__clear" onClick={() => setFilters({})}>
              <FiX />
              <span>
                {t('contracts.table.clearColumnFilters', { count: activeFilterCount, defaultValue: `Clear filters (${activeFilterCount})` })}
              </span>
            </button>
          )}
        </div>
        <div className="cl-grid-toolbar__right">
          <div className="cl-grid-toolbar__menu-anchor">
            <button
              type="button"
              data-menu-trigger
              className={`cl-btn cl-btn--ghost ${openMenu === 'cols' ? 'cl-btn--pressed' : ''}`}
              onClick={(e) => toggleMenu('cols', e)}
            >
              <FiColumns />
              <span>{t('contracts.table.columns', { defaultValue: 'Columns' })}</span>
            </button>
            {openMenu === 'cols' && createPortal(
              <div className="cl-th-menu" style={menuPos ? { top: menuPos.top, left: menuPos.left } : undefined}>
                <div className="cl-th-menu__list">
                  {COLUMN_DEFS.map((c) => (
                    <label key={c.key} className="cl-th-menu__option">
                      <input
                        type="checkbox"
                        checked={visibleKeys.includes(c.key)}
                        disabled={visibleKeys.includes(c.key) && visibleKeys.length === 1}
                        onChange={() => {
                          setVisibleKeys((prev) => prev.includes(c.key)
                            ? prev.filter((k) => k !== c.key)
                            : [...COLUMN_DEFS.map((d) => d.key).filter((k) => prev.includes(k) || k === c.key)]);
                        }}
                      />
                      <span>{colLabel(c.key)}</span>
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  className="cl-th-menu__clear"
                  onClick={() => setVisibleKeys(DEFAULT_VISIBLE)}
                >
                  {t('contracts.table.resetColumns', { defaultValue: 'Reset columns' })}
                </button>
              </div>,
              document.body
            )}
          </div>
          <button
            type="button"
            className="cl-btn cl-btn--ghost"
            onClick={exportExcel}
            disabled={showSkeleton || sortedRows.length === 0}
          >
            <FiDownload />
            <span>{t('contracts.table.exportExcel', { defaultValue: 'Export to Excel' })}</span>
          </button>
          <button
            type="button"
            className={`cl-btn cl-btn--ghost ${fullscreen ? 'cl-btn--pressed' : ''}`}
            onClick={() => setFullscreen((on) => !on)}
            title={fullscreen
              ? t('contracts.table.exitFullscreen', { defaultValue: 'Exit full screen (Esc)' })
              : t('contracts.table.fullscreen', { defaultValue: 'Full screen' })}
          >
            {fullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
            <span>
              {fullscreen
                ? t('contracts.table.exitFullscreen', { defaultValue: 'Exit full screen' })
                : t('contracts.table.fullscreen', { defaultValue: 'Full screen' })}
            </span>
          </button>
        </div>
      </div>

      {/* The pinned copy of the header — same cells and handlers, laid out to
          the measured column widths and slid along with the table's own
          horizontal scroll. Hidden until the real header leaves the fold.

          Full screen does not need it: there the scroll box is the viewport
          panel itself, so the real `thead` can stick to it natively. */}
      {!fullscreen && (
        <div
          className={`cl-sticky-head ${stickyHead.active ? 'is-on' : ''}`}
          style={{ marginBottom: stickyHead.headHeight ? -stickyHead.headHeight : 0 }}
          aria-hidden="true"
        >
          <div className="cl-sticky-head__inner" ref={stickyHead.innerRef}>
            <table
              className="cl-table cl-table--pinned"
              style={stickyHead.tableWidth ? { width: stickyHead.tableWidth } : undefined}
            >
              <colgroup>
                {stickyHead.widths.map((w, i) => (
                  <col key={visibleCols[i]?.key || i} style={{ width: w }} />
                ))}
              </colgroup>
              <thead>{renderHeadRow(true)}</thead>
            </table>
          </div>
        </div>
      )}

      <div className="cl-table-scroll" ref={stickyHead.scrollRef}>
        <table className="cl-table">
          <thead ref={stickyHead.headRef}>{renderHeadRow(false)}</thead>
          <tbody>
            {showSkeleton && renderSkeletonRows()}
            {!showSkeleton && sortedRows.length === 0 && (
              <tr>
                <td colSpan={colCount}>
                  <div className="cl-table-empty">
                    <FiInbox />
                    <span>{t('contracts.table.noFilterMatches', { defaultValue: 'No contracts match the current column filters.' })}</span>
                    {activeFilterCount > 0 && (
                      <button type="button" className="cl-filters__clear" onClick={() => setFilters({})}>
                        <FiX />
                        <span>{t('contracts.table.clearColumnFilters', { count: activeFilterCount, defaultValue: 'Clear filters' })}</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {!showSkeleton && !groups && sortedRows.map((c, index) => (
              <tr
                key={c._id}
                className="cl-row"
                style={{ animationDelay: `${Math.min(index, 20) * 25}ms` }}
                onClick={() => openContract(c)}
              >
                {visibleCols.map((col) => renderCell(col, c))}
              </tr>
            ))}
            {!showSkeleton && groups && groups.map((g) => (
              <React.Fragment key={g.label}>
                <tr className="cl-group-row" onClick={() => toggleGroup(g.label)}>
                  <td colSpan={colCount}>
                    <div className="cl-group-row__inner">
                      <span className={`cl-group-row__chevron ${collapsed.has(g.label) ? '' : 'cl-group-row__chevron--open'}`}>
                        <FiChevronRight />
                      </span>
                      <span className="cl-group-row__label">{g.label}</span>
                      <span className="cl-group-row__count">{g.rows.length}</span>
                      {g.total > 0 && (
                        <span className="cl-group-row__total">
                          {t('contracts.table.groupTotal', { defaultValue: 'Total' })}: {formatMoney(g.total)}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                {!collapsed.has(g.label) && g.rows.map((c) => (
                  <tr key={c._id} className="cl-row" onClick={() => openContract(c)}>
                    {visibleCols.map((col) => renderCell(col, c))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* The table's own scrollbar would sit at the bottom of a page-length
          table. This one rides the fold instead, and drives it. Full screen
          has a real bottom edge, so the native scrollbar is enough there. */}
      {!fullscreen && stickyHead.overflowing && (
        <div className="cl-hscroll" ref={stickyHead.barRef}>
          <div className="cl-hscroll__spacer" style={{ width: stickyHead.tableWidth }} />
        </div>
      )}

      {!showSkeleton && (
        <div className="cl-table-footer">
          <div className="cl-table-footer__pagesize">
            <SearchableSelect
              value={String(pageSize)}
              onChange={(v) => onPageSizeChange(Number(v))}
              options={[20, 50, 100].map((n) => ({
                value: String(n),
                label: t('contracts.table.rowsPerPage', { count: n, defaultValue: `${n} / page` }),
              }))}
            />
          </div>
          {renderPagination()}
        </div>
      )}
    </div>
  );

  // In full screen the table is a fixed panel, so it is portalled out of the
  // page's stacking and overflow context. This component keeps its state
  // either way — only the DOM parent changes.
  return fullscreen ? createPortal(table, document.body) : table;
}

export default ContractsTable;
